import { useCallback, useEffect, useState } from 'react';

/**
 * Three static routes over the History API.
 *
 * A router dependency would be overkill for this, but view state would not do: public
 * listening makes a track link worth sharing, so `/t/:id` has to be a real URL. The
 * server already cooperates — its not-found handler falls back to `index.html` for any
 * non-API GET, so a cold load of one of these URLs resolves.
 *
 * `/admin` parses for everyone; whether it *renders* is App's decision, and the API
 * refuses a non-owner regardless.
 */

export type Route = { name: 'library' } | { name: 'track'; id: string } | { name: 'admin' };

export function parseRoute(pathname: string): Route {
  const match = /^\/t\/([^/]+)\/?$/.exec(pathname);
  if (match?.[1]) return { name: 'track', id: decodeURIComponent(match[1]) };
  if (/^\/admin\/?$/.test(pathname)) return { name: 'admin' };
  return { name: 'library' };
}

export function trackPath(id: string): string {
  return `/t/${encodeURIComponent(id)}`;
}

export function useRoute(): { route: Route; navigate: (path: string) => void } {
  const [route, setRoute] = useState<Route>(() =>
    parseRoute(typeof window === 'undefined' ? '/' : window.location.pathname),
  );

  useEffect(() => {
    const onPopState = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((path: string) => {
    if (window.location.pathname !== path) window.history.pushState({}, '', path);
    setRoute(parseRoute(path));
  }, []);

  return { route, navigate };
}
